import { useTranslation } from 'next-i18next'
import { memo } from 'react'

import { Parking } from '@/service'
import { getParkingTagIcon, InfoTagType } from '@/constants/infoTagsAssets'

import { useIsMobile } from '@/utils/useMobile'

import { styles } from './ParkingTags.styled'

type ParkingTagsProps = {
  parking: Parking
  short?: boolean
  positionAbsolute?: boolean
  noMargin?: boolean
  isResultLabels?: boolean
}

const ParkingTags = ({
  parking,
  short = false,
  positionAbsolute = true,
  noMargin = false,
  isResultLabels = false
}: ParkingTagsProps) => {
  const { isMobile } = useIsMobile()
  const { t } = useTranslation('common')

  const hasAnyTags =
    parking.subscriptionAvailable ||
    parking.reservationAvailable ||
    (parking.infoTags && parking.infoTags.length > 0)

  if (!hasAnyTags) {
    return <div />
  }

  const labelsStyle = [
    styles.labels,
    short &&
      (isResultLabels
        ? styles.resultLabelsShort(positionAbsolute, noMargin)
        : styles.labelsShort(positionAbsolute, noMargin))
  ]

  const labelStyle = [styles.label, short && styles.labelShort]
  const hoverStyle = [short && styles.hover]

  const tagsMap = {
    AUTOPAY: (
      <li css={[...labelStyle, styles.labelRed]} key="autopay">
        <img
          src={getParkingTagIcon(InfoTagType.AUTOPAY)}
          alt="Autopay icon"
          width={isMobile ? 11 : 16}
          height={isMobile ? 11 : 16}
        />
        <span css={hoverStyle}>{t('tags.autopay')}</span>
      </li>
    ),
    NAVIPAY: (
      <li css={[...labelStyle, styles.labelLight]} key="navipay">
        <img
          src={getParkingTagIcon(InfoTagType.NAVIPAY_MINI)}
          alt="NaviPay icon"
          width={isMobile ? 11 : 16}
          height={isMobile ? 11 : 16}
        />
        <span css={hoverStyle}>{t('tags.navipay')}</span>
      </li>
    ),
    EV_CHARGERS: (
      <li css={[...labelStyle, styles.labelBlue]} key="ev_chargers">
        <img
          src={getParkingTagIcon(InfoTagType.CHARGING)}
          alt="Charging icon"
          width={isMobile ? 11 : 16}
          height={isMobile ? 11 : 16}
        />
        <span css={hoverStyle}>{t('tags.charging')}</span>
      </li>
    ),
    PARK_AND_FLY: (
      <li css={[...labelStyle, styles.labelGreen]} key="park_and_fly">
        <img
          src={getParkingTagIcon(InfoTagType.PLANE_ICON)}
          alt="Plane icon"
          width={isMobile ? 11 : 16}
          height={isMobile ? 11 : 16}
        />
        <span css={hoverStyle}>{t('tags.parkAndFly')}</span>
      </li>
    ),
    SCOOTERS: (
      <li css={[...labelStyle, styles.labelBlue]} key="scooters">
        <img
          src={getParkingTagIcon(InfoTagType.SCOOTER_ICON)}
          alt="Scooter icon"
          width={isMobile ? 11 : 16}
          height={isMobile ? 11 : 16}
        />
        <span css={hoverStyle}>{t('tags.scooters')}</span>
      </li>
    ),
    VALET: (
      <li css={[...labelStyle, styles.labelBlue]} key="valet">
        <img
          src={getParkingTagIcon(InfoTagType.VALET_ICON)}
          alt="Valet icon"
          width={isMobile ? 11 : 16}
          height={isMobile ? 11 : 16}
        />
        <span css={hoverStyle}>{t('tags.valet')}</span>
      </li>
    ),
    CAR_WASH: (
      <li css={[...labelStyle, styles.labelGreen]} key="car_wash">
        <img
          src={getParkingTagIcon(InfoTagType.CAR_WASH_ICON)}
          alt="Car wash icon"
          width={isMobile ? 11 : 16}
          height={isMobile ? 11 : 16}
        />
        <span css={hoverStyle}>{t('tags.carWash')}</span>
      </li>
    )
  }

  const subscriptionTag = parking.subscriptionAvailable && (
    <li css={[...labelStyle, styles.labelDark]} key="subscription">
      <img
        src={getParkingTagIcon(InfoTagType.HINT_SUBSCRIPTION)}
        alt="Calendar icon"
        width={isMobile ? 11 : 16}
        height={isMobile ? 11 : 16}
      />
      <span css={hoverStyle}>{t('tags.subscriptions')}</span>
    </li>
  )

  const reservationTag = parking.reservationAvailable && (
    <li css={[...labelStyle, styles.labelDark]} key="reservation">
      <img
        src={getParkingTagIcon(InfoTagType.HINT_RESERVATION)}
        alt="Clock icon"
        width={isMobile ? 11 : 16}
        height={isMobile ? 11 : 16}
      />
      <span css={hoverStyle}>{t('tags.reservations')}</span>
    </li>
  )

  const infoTags = []

  if (parking.infoTags) {
    for (const tag of parking.infoTags) {
      if (tagsMap[tag]) {
        infoTags.push(tagsMap[tag])
      }
    }
  }

  return (
    <ul css={labelsStyle}>
      {subscriptionTag}
      {reservationTag}
      {infoTags}
    </ul>
  )
}

export default memo(ParkingTags)
